import { useState, useEffect, useMemo } from "react"
import { Search, Bell, CheckCircle, XCircle, Clock, AlertCircle, User, ChevronLeft, ChevronRight, Users, FileText, Table2, PieChart as PieChartIcon, BarChart3 } from "lucide-react"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

import { EnrollmentApi } from "@/features/enrollment"
import { ApplicationStatusFunnel } from "./ApplicationStatusFunnel"
import { getCampaignSteps } from "@/features/enrollment/core/config/campaigns.config"

type ViewMode = 'table' | 'chart'

interface PendingApplicationViewModel {
  id: string
  clientName: string
  email: string
  submittedAt: string
  currentStage: number
  stageName: string
  status: string
  priority: "low" | "medium" | "high" | "urgent"
  gamerTagsSkipped?: boolean
  campaignId?: string
  totalSteps: number
}

interface AgentPendingApplicationsProps {
  selectedCampaign?: { id: string } | null
}

export default function AgentPendingApplications({ selectedCampaign }: AgentPendingApplicationsProps = {}) {
  const [activeTab, setActiveTab] = useState<"client-enrollment" | "self-enrollment" | "status-funnel">("client-enrollment")
  const [viewMode, setViewMode] = useState<ViewMode>('table')

  // Client Enrollment Applications
  const [clientApplications, setClientApplications] = useState<PendingApplicationViewModel[]>([])
  const [filteredClientApplications, setFilteredClientApplications] = useState<PendingApplicationViewModel[]>([])
  const [paginatedClientApplications, setPaginatedClientApplications] = useState<PendingApplicationViewModel[]>([])

  // Self Enrollment Applications
  const [selfApplications, setSelfApplications] = useState<PendingApplicationViewModel[]>([])
  const [filteredSelfApplications, setFilteredSelfApplications] = useState<PendingApplicationViewModel[]>([])
  const [paginatedSelfApplications, setPaginatedSelfApplications] = useState<PendingApplicationViewModel[]>([])


  const [loading, setLoading] = useState(true)
  const [resumingApplicationId, setResumingApplicationId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [stageFilter, setStageFilter] = useState<number | null>(null)
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(8)
  const [totalPages, setTotalPages] = useState(0)
  const [totalClientCount, setTotalClientCount] = useState(0)
  const [totalSelfCount, setTotalSelfCount] = useState(0)

  // Load live sessions for agent and map to view model (show all statuses)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)

        const [clientResult, selfResult] = await Promise.all([
          EnrollmentApi.listSessions({
            enrollmentType: 'CLIENT_ENROLLMENT',
            status: statusFilter === "all" ? undefined : statusFilter,
            stage: stageFilter || undefined,
            campaignId: selectedCampaign?.id,
            search: searchTerm || undefined,
            page: currentPage,
            limit: itemsPerPage
          }),
          EnrollmentApi.listSessions({
            enrollmentType: 'SELF_ENROLLMENT',
            status: statusFilter === "all" ? undefined : statusFilter,
            stage: stageFilter || undefined,
            campaignId: selectedCampaign?.id,
            search: searchTerm || undefined,
            page: currentPage,
            limit: itemsPerPage
          })
        ])

        const clientEnrollmentSessions = clientResult.sessions || []
        const selfEnrollmentSessions = selfResult.sessions || []


        const getStageName = (stage: number): string => {
          const stages = {
            1: "Gaming Platform",
            2: "Intake Contact",
            3: "Injured Party",
            4: "Gaming Details",
            5: "Health & Legal",
            6: "Emergency Notes",
            7: "Identity Preview",
            8: "Submit",
            9: "Signature",
            10: "Follow Up"
          }
          return stages[stage as keyof typeof stages] || `Step ${stage}`
        }

        const getApplicationStatusStep = (session: any): number => {
          const data = (session.data as any) || {}

          if (data.isLeftBookOfBusiness === true) return 8
          if (data.isServiceEnded === true) return 7
          if (session.status === 'COMPLETED') return 6
          if (session.status === 'SUBMITTED' && data.hasFollowUpFlags === true) return 5
          if (session.status === 'PENDING') return 4
          if (session.status === 'IN_PROGRESS') {
            if (data.aiRobotResult === 'half-success' || data.aiRobotResult === 'full-success') return 3
            if (data.aiRobotAborted === true) return 2
            // Use actual currentStepIndex from database (0-based), convert to 1-based for display
            const dbStepIndex = session.currentStepIndex
            if (typeof dbStepIndex === 'number' && dbStepIndex >= 0) {
              return dbStepIndex + 1 // Convert 0-based to 1-based
            }
            return 1
          }
          return 1
        }

        const getApplicationStatusName = (step: number): string => {
          const names: Record<number, string> = {
            1: "New lead",
            2: "Need Addtl Data",
            3: "Authenticated",
            4: "Pending Consent",
            5: "Submitted - Addtl Docs",
            6: "Enrolled",
            7: "Service Ended",
            8: "Left BoB"
          }
          return names[step] || "Unknown"
        }


        const mappedClient: PendingApplicationViewModel[] = clientEnrollmentSessions.map((s: any) => {


          // Extract client name from new step IDs first, then fallback to old step IDs
          const clientName =
            (s.data?.intakeContact?.intakeFirstName && s.data?.intakeContact?.intakeLastName
              ? `${s.data.intakeContact.intakeFirstName} ${s.data.intakeContact.intakeLastName}`
              : s.data?.injuredParty?.injuredPartyFirstName && s.data?.injuredParty?.injuredPartyLastName
                ? `${s.data.injuredParty.injuredPartyFirstName} ${s.data.injuredParty.injuredPartyLastName}`
                : s.data?.step2?.intakeFirstName && s.data?.step2?.intakeLastName
                  ? `${s.data.step2.intakeFirstName} ${s.data.step2.intakeLastName}`
                  : s.data?.step3?.injuredPartyFirstName && s.data?.step3?.injuredPartyLastName
                    ? `${s.data.step3.injuredPartyFirstName} ${s.data.step3.injuredPartyLastName}`
                    : "-")

          // Extract email from new step IDs first, then fallback to old step IDs
          const email =
            s.data?.intakeContact?.clientEmail ||
            s.data?.intakeContact?.intakeEmail ||
            s.data?.step2?.clientEmail ||
            s.data?.step2?.intakeEmail ||
            "-"

          const appStatusStep = getApplicationStatusStep(s)
          const campaignId = s.campaignId || s.data?.selectedCampaign || s.data?.campaignId
          const campaignSteps = getCampaignSteps(campaignId)
          const totalSteps = campaignSteps.length || 8 // Default to 8 if unknown

          return {
            id: s.id,
            clientName,
            email,
            submittedAt: s.updatedAt,
            currentStage: appStatusStep,
            stageName: getApplicationStatusName(appStatusStep),
            status: s.status,
            priority: "medium",
            gamerTagsSkipped: s.data?.gamingPlatform?.gamerTagsSkipped || s.data?.step1?.gamerTagsSkipped || false,
            campaignId,
            totalSteps,
          }
        })

        const mappedSelf: PendingApplicationViewModel[] = selfEnrollmentSessions.map((s: any) => {

          // Extract client name from new step IDs first, then fallback to old step IDs
          const clientName =
            (s.data?.intakeContact?.intakeFirstName && s.data?.intakeContact?.intakeLastName
              ? `${s.data.intakeContact.intakeFirstName} ${s.data.intakeContact.intakeLastName}`
              : s.data?.injuredParty?.injuredPartyFirstName && s.data?.injuredParty?.injuredPartyLastName
                ? `${s.data.injuredParty.injuredPartyFirstName} ${s.data.injuredParty.injuredPartyLastName}`
                : s.data?.step2?.intakeFirstName && s.data?.step2?.intakeLastName
                  ? `${s.data.step2.intakeFirstName} ${s.data.step2.intakeLastName}`
                  : s.data?.step3?.injuredPartyFirstName && s.data?.step3?.injuredPartyLastName
                    ? `${s.data.step3.injuredPartyFirstName} ${s.data.step3.injuredPartyLastName}`
                    : "-")

          // Extract email from new step IDs first, then fallback to old step IDs
          const email =
            s.data?.intakeContact?.clientEmail ||
            s.data?.intakeContact?.intakeEmail ||
            s.data?.step2?.clientEmail ||
            s.data?.step2?.intakeEmail ||
            "-"

          const appStatusStep = getApplicationStatusStep(s)
          const campaignId = s.campaignId || s.data?.selectedCampaign || s.data?.campaignId
          const campaignSteps = getCampaignSteps(campaignId)
          const totalSteps = campaignSteps.length || 8 // Default to 8 if unknown

          return {
            id: s.id,
            clientName,
            email,
            submittedAt: s.updatedAt,
            currentStage: appStatusStep,
            stageName: getApplicationStatusName(appStatusStep),
            status: s.status,
            priority: "medium",
            gamerTagsSkipped: s.data?.gamingPlatform?.gamerTagsSkipped || s.data?.step1?.gamerTagsSkipped || false,
            campaignId,
            totalSteps,
          }
        })

        setClientApplications(mappedClient)
        setFilteredClientApplications(mappedClient)
        setSelfApplications(mappedSelf)
        setFilteredSelfApplications(mappedSelf)

        if (clientResult.pagination) {
          setTotalPages(clientResult.pagination.totalPages)
          setTotalClientCount(clientResult.pagination.total)
        }
        if (selfResult.pagination) {
          setTotalSelfCount(selfResult.pagination.total)
        }
      } finally {
        setLoading(false)
      }
    }
    void load()
    const onPendingUpdated = () => void load()
    window.addEventListener('pending:updated', onPendingUpdated as any)
    return () => window.removeEventListener('pending:updated', onPendingUpdated as any)
  }, [statusFilter, stageFilter, currentPage, itemsPerPage, selectedCampaign])

  useEffect(() => {
    const filtered = (clientApplications || []).filter((app: PendingApplicationViewModel) => {
      const matchesSearch =
        app.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.id.toLowerCase().includes(searchTerm.toLowerCase())

      return matchesSearch
    })

    filtered.sort((a: PendingApplicationViewModel, b: PendingApplicationViewModel) =>
      new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    )

    setFilteredClientApplications(filtered)
  }, [clientApplications, searchTerm, activeTab])

  useEffect(() => {
    const filtered = (selfApplications || []).filter((app: PendingApplicationViewModel) => {
      const matchesSearch =
        app.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.id.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus = statusFilter === "all" || app.status === statusFilter

      return matchesSearch && matchesStatus
    })

    filtered.sort((a: PendingApplicationViewModel, b: PendingApplicationViewModel) =>
      new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    )

    setFilteredSelfApplications(filtered)
  }, [selfApplications, searchTerm, activeTab])


  useEffect(() => {
    // With server-side pagination, we don't need to slice locally anymore
    if (activeTab === "client-enrollment") {
      setPaginatedClientApplications(filteredClientApplications)
    } else {
      setPaginatedSelfApplications(filteredSelfApplications)
    }
  }, [filteredClientApplications, filteredSelfApplications, activeTab])

  const handleStatusChange = async (applicationId: string, newStatus: PendingApplicationViewModel["status"]) => {
    setUpdatingStatus(applicationId)

    try {
      await new Promise((resolve) => setTimeout(resolve, 500))

      if (activeTab === "client-enrollment") {
        setClientApplications((prevApps: PendingApplicationViewModel[]) =>
          prevApps.map((app: PendingApplicationViewModel) => (app.id === applicationId ? { ...app, status: newStatus } : app))
        )
      } else {
        setSelfApplications((prevApps: PendingApplicationViewModel[]) =>
          prevApps.map((app: PendingApplicationViewModel) => (app.id === applicationId ? { ...app, status: newStatus } : app))
        )
      }

    } catch (error) {
      console.error("Failed to update status:", error)
    } finally {
      setUpdatingStatus(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />
      case "under_review":
        return <Bell className="w-4 h-4 text-blue-500" />
      case "approved":
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-500" />
      case "requires_info":
        return <AlertCircle className="w-4 h-4 text-orange-500" />
      default:
        return <Clock className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "text-yellow-600"
      case "in_progress":
        return "text-blue-600"
      case "submitted":
        return "text-green-600"
      case "completed":
        return "text-green-600"
      case "cancelled":
        return "text-red-600"
      case "signed":
        return "text-green-600"
      default:
        return "text-gray-600"
    }
  }

  const getPriorityDot = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-500"
      case "high":
        return "bg-orange-500"
      case "medium":
        return "bg-yellow-500"
      case "low":
        return "bg-green-500"
      default:
        return "bg-gray-500"
    }
  }

  // Chart colors
  const STATUS_COLORS = {
    IN_PROGRESS: '#3b82f6',
    PENDING: '#f59e0b',
    SUBMITTED: '#10b981',
    SIGNED: '#10b981',
    COMPLETED: '#10b981',
    CANCELLED: '#ef4444',
    ERROR: '#ef4444',
  }

  const STAGE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#14b8a6']

  // Get current applications based on active tab
  const currentApplications = activeTab === "client-enrollment"
    ? filteredClientApplications
    : filteredSelfApplications

  // Calculate status counts for summary cards
  const statusCounts = useMemo(() => {
    const counts = {
      approved: 0,
      rejected: 0,
      inProgress: 0,
      error: 0,
      total: currentApplications.length
    }

    currentApplications.forEach(app => {
      const status = app.status.toUpperCase()
      if (status === 'COMPLETED' || status === 'SIGNED' || status === 'SUBMITTED') {
        counts.approved++
      } else if (status === 'CANCELLED') {
        counts.rejected++
      } else if (status === 'IN_PROGRESS' || status === 'PENDING') {
        counts.inProgress++
      } else {
        counts.error++
      }
    })

    return counts
  }, [currentApplications])

  // Prepare chart data - Status distribution (Pie Chart)
  const statusChartData = useMemo(() => {
    const statusMap = new Map<string, number>()

    currentApplications.forEach(app => {
      const status = app.status.toUpperCase()
      statusMap.set(status, (statusMap.get(status) || 0) + 1)
    })

    return Array.from(statusMap.entries())
      .map(([name, value]) => ({
        name: name.replace('_', ' '),
        value,
        fill: STATUS_COLORS[name as keyof typeof STATUS_COLORS] || '#6b7280'
      }))
      .filter(item => item.value > 0)
  }, [currentApplications])

  // Prepare chart data - Applications trend over time (Line Chart)
  const trendChartData = useMemo(() => {
    const grouped = new Map<string, { total: number; completed: number }>()

    currentApplications.forEach(app => {
      const date = new Date(app.submittedAt).toISOString().split('T')[0]
      const existing = grouped.get(date) || { total: 0, completed: 0 }
      const status = app.status.toUpperCase()
      grouped.set(date, {
        total: existing.total + 1,
        completed: existing.completed + (['COMPLETED', 'SIGNED', 'SUBMITTED'].includes(status) ? 1 : 0),
      })
    })

    return Array.from(grouped.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [currentApplications])

  // Prepare chart data - Applications by stage (Bar Chart)
  const stageChartData = useMemo(() => {
    const stageMap = new Map<string, number>()

    currentApplications.forEach(app => {
      const stageName = app.stageName || `Step ${app.currentStage}`
      stageMap.set(stageName, (stageMap.get(stageName) || 0) + 1)
    })

    return Array.from(stageMap.entries())
      .map(([name, count], index) => ({
        name,
        count,
        fill: STAGE_COLORS[index % STAGE_COLORS.length]
      }))
      .sort((a, b) => b.count - a.count)
  }, [currentApplications])

  const getTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return "Just now"
    if (diffInHours < 24) return `${diffInHours}h ago`
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d ago`
    return date.toLocaleDateString()
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const getPaginationRange = () => {
    const delta = 2
    const range = []
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...")
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  // IMPROVED: Better resume handler with loading state and user feedback
  const handleResumeClick = async (application: PendingApplicationViewModel) => {
    // Set loading state
    setResumingApplicationId(application.id)

    // Show loading notification
    const loadingNotification = document.createElement('div')
    loadingNotification.className = 'fixed top-4 right-4 bg-blue-500 text-white px-6 py-3 rounded-md shadow-lg z-50 transition-opacity duration-300 flex items-center space-x-3'
    loadingNotification.innerHTML = `
      <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
      <div>
        <div class="font-medium">Recovering Session</div>
        <div class="text-sm opacity-90">Please wait while we restore your previous session...</div>
      </div>
    `
    document.body.appendChild(loadingNotification)

    try {


      // Step 1: Call resume API
      const resumedSession = await EnrollmentApi.resume(application.id)


      // Step 2: Update localStorage
      localStorage.setItem('enrollmentSessionId', application.id)
      const backendStepIndex = application.currentStage - 1
      localStorage.setItem('enrollmentCurrentStep', backendStepIndex.toString())
      localStorage.setItem('enrollmentCampaign', application.campaignId || '')


      // Step 3: Dispatch navigation event
      const navigationStepIndex = application.currentStage - 1
      const event = new CustomEvent('navigate:enrollment', {
        detail: {
          sessionId: application.id,
          currentStep: navigationStepIndex,
          stageName: application.stageName,
          resumedSession: resumedSession // ✅ Pass the full session data
        }
      })
      window.dispatchEvent(event)

      // Update notification to success
      loadingNotification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-md shadow-lg z-50 transition-opacity duration-300 flex items-center space-x-3'
      loadingNotification.innerHTML = `
        <div class="text-green-100">✓</div>
        <div>
          <div class="font-medium">Session Recovered</div>
          <div class="text-sm opacity-90">Redirecting to step ${application.currentStage}...</div>
        </div>
      `

      // Remove notification after 2 seconds
      setTimeout(() => {
        loadingNotification.style.opacity = '0'
        setTimeout(() => {
          if (document.body.contains(loadingNotification)) {
            document.body.removeChild(loadingNotification)
          }
        }, 300)
      }, 2000)

    } catch (error) {
      console.error('❌ Resume process failed:', error)

      // Update notification to error
      loadingNotification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-md shadow-lg z-50 transition-opacity duration-300 flex items-center space-x-3'
      loadingNotification.innerHTML = `
        <div class="text-red-100">✗</div>
        <div>
          <div class="font-medium">Resume Failed</div>
          <div class="text-sm opacity-90">Please try again or contact support.</div>
        </div>
      `

      // Remove notification after 4 seconds
      setTimeout(() => {
        loadingNotification.style.opacity = '0'
        setTimeout(() => {
          if (document.body.contains(loadingNotification)) {
            document.body.removeChild(loadingNotification)
          }
        }, 300)
      }, 4000)
    } finally {
      // Clear loading state
      setResumingApplicationId(null)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Shimmer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-48 mt-2 animate-pulse"></div>
          </div>
        </div>

        {/* Tab Navigation Shimmer */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <div className="py-2 px-1 border-b-2 border-blue-500">
              <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
            </div>
            <div className="py-2 px-1">
              <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
            </div>
          </nav>
        </div>

        {/* Search and Filter Shimmer */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-10 bg-gray-200 rounded-lg w-32 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-32 animate-pulse"></div>
          </div>
        </div>

        {/* Table Shimmer */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3">
                <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
              <div className="col-span-2">
                <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
              </div>
              <div className="col-span-2">
                <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
              <div className="col-span-2">
                <div className="h-4 bg-gray-200 rounded w-14 animate-pulse"></div>
              </div>
              <div className="col-span-2">
                <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
              </div>
              <div className="col-span-1">
                <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Table Rows Shimmer */}
          <div className="divide-y divide-gray-200">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="px-6 py-4">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                      <div>
                        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mb-1"></div>
                        <div className="h-3 bg-gray-200 rounded w-32 animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  </div>
                  <div className="col-span-2">
                    <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
                  </div>
                  <div className="col-span-2">
                    <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                  <div className="col-span-2">
                    <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  </div>
                  <div className="col-span-1">
                    <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Shimmer */}
        <div className="flex items-center justify-between">
          <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="flex items-center space-x-2">
            <div className="h-8 bg-gray-200 rounded w-8 animate-pulse"></div>
            <div className="h-8 bg-gray-200 rounded w-8 animate-pulse"></div>
            <div className="h-8 bg-gray-200 rounded w-8 animate-pulse"></div>
            <div className="h-8 bg-gray-200 rounded w-8 animate-pulse"></div>
            <div className="h-8 bg-gray-200 rounded w-8 animate-pulse"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Applications</h2>
          <p className="text-gray-600 mt-1">
            {activeTab === "client-enrollment"
              ? `${filteredClientApplications.length} client enrollment applications`
              : `${filteredSelfApplications.length} self enrollment applications`
            }
          </p>
        </div>
        {/* View Toggle */}
        <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
          <button
            onClick={() => setViewMode('table')}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'table'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            <Table2 className="w-4 h-4" />
            <span>Table</span>
          </button>
          <button
            onClick={() => setViewMode('chart')}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'chart'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            <PieChartIcon className="w-4 h-4" />
            <span>Charts</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab("client-enrollment")
              setCurrentPage(1)
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "client-enrollment"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Client Enrollment ({totalClientCount})
          </button>
          <button
            onClick={() => {
              setActiveTab("self-enrollment")
              setCurrentPage(1)
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "self-enrollment"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Self Enrollment ({totalSelfCount})
          </button>
          <button
            onClick={() => setActiveTab("status-funnel")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "status-funnel"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            8 Step Status Funnel
          </button>
        </nav>
      </div>

      {/* Status Funnel Tab Content */}
      {activeTab === "status-funnel" && (
        <div className="mt-4">
          <ApplicationStatusFunnel campaignId={selectedCampaign?.id} />
        </div>
      )}

      {/* Search, Filters, and Table - only show for client/self enrollment tabs */}
      {activeTab !== "status-funnel" && (
        <>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search applications..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="PENDING">Pending</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="SIGNED">Signed</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <label className="text-sm font-medium text-gray-700">Stage:</label>
                <select
                  value={stageFilter || ""}
                  onChange={(e) => {
                    setStageFilter(e.target.value ? Number(e.target.value) : null)
                    setCurrentPage(1)
                  }}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">All Stages</option>
                  <option value={1}>1: New lead</option>
                  <option value={2}>2: Need Addtl Data</option>
                  <option value={3}>3: Authenticated</option>
                  <option value={4}>4: Pending Consent</option>
                  <option value={5}>5: Submitted - Addtl Docs</option>
                  <option value={6}>6: Enrolled</option>
                  <option value={7}>7: Service Ended</option>
                  <option value={8}>8: Left BoB</option>
                </select>
                <label className="text-sm font-medium text-gray-700">Show:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value={8}>8</option>
                  <option value={16}>16</option>
                  <option value={24}>24</option>
                  <option value={32}>32</option>
                </select>
              </div>
            </div>
          </div>

          {/* Content - Table or Charts based on viewMode */}
          {viewMode === 'table' ? (
            /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Session ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stage
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(activeTab === "client-enrollment"
                      ? paginatedClientApplications
                      : paginatedSelfApplications).map((application: PendingApplicationViewModel) => (
                        <tr key={application.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{application.clientName}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{application.email}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 font-mono">
                                {application.id.substring(0, 8)}...
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900 flex items-center space-x-2">
                                {application.currentStage === 1 && application.gamerTagsSkipped ? (
                                  <>
                                    <span>⚠️ Gamer Tags Skipped</span>
                                    <span className="text-yellow-500">?</span>
                                  </>
                                ) : (
                                  application.stageName
                                )}
                              </div>
                              <div className="text-gray-500">Step {application.currentStage}/{application.totalSteps}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-sm font-medium capitalize ${getStatusColor(application.status)}`}>
                              {application.status.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {getTimeAgo(application.submittedAt)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            {application.status !== "COMPLETED" ? (
                              <button
                                className={`btn-primary text-sm flex items-center space-x-2 ${resumingApplicationId === application.id ? 'opacity-75 cursor-not-allowed' : ''
                                  }`}
                                onClick={() => handleResumeClick(application)}
                                disabled={resumingApplicationId === application.id}
                              >
                                {resumingApplicationId === application.id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Recovering...</span>
                                  </>
                                ) : (
                                  <span>Resume</span>
                                )}
                              </button>
                            ) : (
                              <button
                                className="focus:outline-none text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                                onClick={() => {
                                  (async () => {
                                    try {
                                      localStorage.setItem('enrollmentSessionId', application.id)
                                      // Navigate to the last step (FollowUp step) - step 9 (0-based index)
                                      localStorage.setItem('enrollmentCurrentStep', '9')
                                      localStorage.setItem('enrollmentCampaign', application.campaignId || '')
                                      localStorage.setItem('enrollmentCampaignName', 'Video Game Addiction') // Default campaign name
                                    } catch { }
                                    try {
                                      // Dispatch navigation event to go to enrollment wizard
                                      window.dispatchEvent(new CustomEvent('navigate:enrollment', {
                                        detail: {
                                          sessionId: application.id,
                                          currentStep: 9, // FollowUp step (0-based index)
                                          stageName: 'Follow Up'
                                        }
                                      }))
                                    } catch { }
                                  })()
                                }}
                              >
                                Followup
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination - keeping original */}
              {((activeTab === "client-enrollment" ? filteredClientApplications.length : filteredSelfApplications.length) > 0) && (
                <div className="bg-white rounded-lg shadow-sm border-t border-gray-100 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <span>
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, activeTab === "client-enrollment"
                          ? totalClientCount
                          : totalSelfCount)} of {activeTab === "client-enrollment"
                            ? totalClientCount
                            : totalSelfCount}{" "}
                        results
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </button>
                      <div className="flex items-center space-x-1">
                        {getPaginationRange().map((page, index) => (
                          <button
                            key={index}
                            onClick={() => (typeof page === "number" ? handlePageChange(page) : null)}
                            disabled={page === "..."}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${page === currentPage ? "bg-blue-600 text-white" : page === "..." ? "text-gray-400 cursor-default" : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"}`}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Chart View */
            <div className="space-y-6">
              {currentApplications.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 text-center py-12">
                  <PieChartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No data available for charts</p>
                  <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or add some applications.</p>
                </div>
              ) : (
                <>
                  {/* Status Distribution Chart (Pie Chart) */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900">Applications by Status</h3>
                    {statusChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={statusChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {statusChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-gray-500">
                        No status data available
                      </div>
                    )}
                  </div>

                  {/* Applications Trend Chart (Line Chart) */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900">Applications Trend Over Time</h3>
                    {trendChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={trendChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                          <Tooltip
                            labelFormatter={(label) => new Date(label as string).toLocaleDateString()}
                            formatter={(value, name) => [value ?? 0, name === 'total' ? 'Total Applications' : 'Completed']}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            name="Total Applications"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="completed"
                            stroke="#10b981"
                            strokeWidth={2}
                            name="Completed"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-gray-500">
                        No trend data available
                      </div>
                    )}
                  </div>

                  {/* Applications by Stage Chart (Bar Chart) */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900">Applications by Stage</h3>
                    {stageChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={stageChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                          <YAxis
                            dataKey="name"
                            type="category"
                            tick={{ fontSize: 12 }}
                            width={120}
                          />
                          <Tooltip />
                          <Bar dataKey="count" name="Applications" radius={[0, 4, 4, 0]}>
                            {stageChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-gray-500">
                        No stage data available
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {viewMode === 'table' && (activeTab === "client-enrollment"
            ? filteredClientApplications.length
            : filteredSelfApplications.length) === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-100">
                <User className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No applications found</h3>
                <p className="mt-2 text-sm text-gray-500">
                  {searchTerm ? "Try adjusting your search criteria." : `No ${activeTab === "client-enrollment"
                    ? "client enrollment"
                    : "self enrollment"} applications at this time.`}
                </p>
              </div>
            )}
        </>
      )}
    </div>
  )
}